import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './HomePage.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentScene, setCurrentScene] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    
    // Cinematic auto-progression through scenes
    const sceneTimer = setInterval(() => {
      setCurrentScene((prev) => (prev + 1) % 4); // 4 scenes total
    }, 4000); // 4 seconds per scene
    
    return () => clearInterval(sceneTimer);
  }, []);

  const stats = [
    { number: "10K+", label: "Active Users" },
    { number: "50K+", label: "Warranties Protected" },
    { number: "99.9%", label: "Uptime" },
    { number: "24/7", label: "Support" }
  ];

  const features = [
    {
      icon: "/icons/warranty-shield.svg",
      title: "Warranty Protection",
      description: "Secure digital warranty management with blockchain verification and enterprise-grade security",
      color: "from-emerald-400 to-emerald-600"
    },
    {
      icon: "/icons/ocr-camera.svg",
      title: "OCR Technology",
      description: "Extract warranty details instantly from photos using advanced AI and machine learning",
      color: "from-amber-400 to-amber-600"
    },
    {
      icon: "/icons/cloud-storage.svg",
      title: "Cloud Storage",
      description: "Access your warranties anytime, anywhere with secure cloud synchronization and backup",
      color: "from-blue-400 to-blue-600"
    },
    {
      icon: "/icons/smart-analytics.svg",
      title: "Smart Analytics",
      description: "Get comprehensive insights about your warranty portfolio with predictive analytics",
      color: "from-purple-400 to-purple-600"
    },
    {
      icon: "/icons/enterprise-security.svg",
      title: "Enterprise Security",
      description: "Bank-level encryption, multi-factor authentication, and compliance with global standards",
      color: "from-red-400 to-red-600"
    },
    {
      icon: "/icons/global-support.svg",
      title: "Global Support",
      description: "Multi-language support and 24/7 customer service for international businesses",
      color: "from-indigo-400 to-indigo-600"
    }
  ];

  const testimonials = [
    {
      name: "Ravi Kumar",
      role: "Electronics Store Owner",
      content: "Suvadi has transformed how I manage customer warranties. The OCR feature saves me hours every week and my customers love the automated reminders!",
      rating: 5
    },
    {
      name: "Priya Devi",
      role: "Home Appliance Retailer",
      content: "The automated reminders and cloud storage have improved our customer service dramatically. No more lost warranties or unhappy customers.",
      rating: 5
    },
    {
      name: "Suresh Industries",
      role: "Manufacturing Company",
      content: "Enterprise-grade security with intuitive design. Perfect for our growing business needs and regulatory compliance requirements!",
      rating: 5
    }
  ];

  return (
    <div className="homepage-container">
      {/* Hero Section with Tamil Heritage */}
      <section className="hero-section">
        <div className="hero-bg-pattern"></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="brand-logo-section">
                <div className="brand-symbol">
                  <img 
                    src="/img/icon-f.png" 
                    alt="Suvadi Logo" 
                    className="brand-logo-img"
                    onError={(e) => {
                      // Fallback to emoji if logo not found
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                  <div className="brand-symbol-fallback">🏺</div>
                </div>
                <h1 className="brand-title">
                  <span className="tamil-title" role="text" aria-label="Suvadi in Tamil">சுவடி</span>
                  <span className="english-title">SUVADI</span>
                </h1>
                <p className="brand-subtitle">Digital Warranty Management Platform</p>
              </div>
              
              <div className="hero-description">
                <h2 className="hero-headline">
                  Secure Digital Warranty Management for Modern Businesses
                </h2>
                <p className="hero-text-desc">
                  Transform your warranty management with AI-powered OCR, cloud storage, 
                  and enterprise-grade security. From small businesses to large enterprises, 
                  protect your customers' warranties with cutting-edge technology.
                </p>
              </div>

              <div className="hero-actions">
                <button 
                  onClick={() => navigate('/register')}
                  className="btn-primary hero-cta"
                  aria-label="Start your free trial of Suvadi warranty management"
                >
                  <span className="btn-text">Start Free Trial</span>
                  <span className="btn-icon">
                    <img src="/icons/arrow-right.svg" alt="" className="btn-arrow" />
                  </span>
                </button>
                <button 
                  onClick={() => navigate('/login')}
                  className="btn-secondary hero-cta-secondary"
                  aria-label="Sign in to your Suvadi account"
                >
                  <span className="btn-text">Sign In</span>
                </button>
              </div>

              <div className="hero-stats">
                {stats.map((stat, index) => (
                  <div key={index} className="stat-item">
                    <div className="stat-number">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hero-visual">
              <div className="cinematic-showcase">
                <div className="showcase-header">
                  <h3 className="cinematic-title">
                    <span className="tamil-accent">சுவடி</span> Business Transformation
                  </h3>
                </div>
                
                <div className="cinematic-container">
                  {/* Scene 1: The Problem */}
                  <div className={`scene scene-problem ${currentScene === 0 ? 'active' : ''}`}>
                    <div className="scene-background">
                      <div className="floating-papers">
                        <div className="paper paper-1"></div>
                        <div className="paper paper-2"></div>
                        <div className="paper paper-3"></div>
                      </div>
                    </div>
                    <div className="scene-content">
                      <div className="scene-icon chaos-icon">
                        <img src="/icons/manual-chaos.svg" alt="Manual Chaos" />
                        <div className="error-pulse"></div>
                      </div>
                      <h4 className="scene-title">The Problem</h4>
                      <p className="scene-text">Lost warranties, manual tracking, frustrated customers</p>
                      <div className="impact-stats">
                        <span className="stat-bad">-$50K Revenue Loss</span>
                      </div>
                    </div>
                  </div>

                  {/* Scene 2: The Solution */}
                  <div className={`scene scene-solution ${currentScene === 1 ? 'active' : ''}`}>
                    <div className="scene-background">
                      <div className="ai-particles">
                        <div className="particle particle-1"></div>
                        <div className="particle particle-2"></div>
                        <div className="particle particle-3"></div>
                        <div className="particle particle-4"></div>
                      </div>
                    </div>
                    <div className="scene-content">
                      <div className="scene-icon solution-icon">
                        <img src="/icons/smart-solution.svg" alt="AI Solution" />
                        <div className="ai-glow"></div>
                      </div>
                      <h4 className="scene-title">AI-Powered Solution</h4>
                      <p className="scene-text">Instant OCR, cloud storage, smart automation</p>
                      <div className="tech-badges">
                        <span className="badge">OCR</span>
                        <span className="badge">AI</span>
                        <span className="badge">Cloud</span>
                      </div>
                    </div>
                  </div>

                  {/* Scene 3: The Transformation */}
                  <div className={`scene scene-transform ${currentScene === 2 ? 'active' : ''}`}>
                    <div className="scene-background">
                      <div className="success-waves">
                        <div className="wave wave-1"></div>
                        <div className="wave wave-2"></div>
                        <div className="wave wave-3"></div>
                      </div>
                    </div>
                    <div className="scene-content">
                      <div className="scene-icon success-icon">
                        <img src="/icons/peace-of-mind.svg" alt="Success" />
                        <div className="success-shine"></div>
                      </div>
                      <h4 className="scene-title">Business Impact</h4>
                      <p className="scene-text">90% efficiency boost, complete compliance</p>
                      <div className="success-metrics">
                        <div className="metric">
                          <span className="metric-value">90%</span>
                          <span className="metric-label">Faster</span>
                        </div>
                        <div className="metric">
                          <span className="metric-value">100%</span>
                          <span className="metric-label">Secure</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Scene 4: Call to Action */}
                  <div className={`scene scene-cta ${currentScene === 3 ? 'active' : ''}`}>
                    <div className="scene-background">
                      <div className="growth-chart">
                        <div className="chart-bar bar-1"></div>
                        <div className="chart-bar bar-2"></div>
                        <div className="chart-bar bar-3"></div>
                        <div className="chart-bar bar-4"></div>
                      </div>
                    </div>
                    <div className="scene-content">
                      <div className="scene-icon cta-icon">
                        <img src="/img/icon-f.png" alt="Suvadi" />
                        <div className="brand-pulse"></div>
                      </div>
                      <h4 className="scene-title">Ready to Transform?</h4>
                      <p className="scene-text">Join 10,000+ businesses already saving time & money</p>
                      <button 
                        onClick={() => navigate('/register')}
                        className="cinematic-cta-btn"
                      >
                        Start Your Journey
                        <img src="/icons/arrow-right.svg" alt="" className="btn-arrow-animated" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Scene Indicators */}
                <div className="scene-indicators">
                  {[0, 1, 2, 3].map((index) => (
                    <button
                      key={index}
                      className={`scene-indicator ${index === currentScene ? 'active' : ''}`}
                      onClick={() => setCurrentScene(index)}
                      aria-label={`Go to scene ${index + 1}`}
                    >
                      <div className="indicator-progress"></div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              Enterprise-Grade Features
            </h2>
            <p className="section-description">
              Powerful tools designed for modern businesses with scalable warranty management
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className={`feature-icon bg-gradient-to-br ${feature.color}`}>
                  <img 
                    src={feature.icon} 
                    alt={feature.title}
                    className="feature-icon-img"
                  />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container">
          <div className="how-it-works-content">
            <div className="how-it-works-text">
              <h2 className="how-it-works-title">
                How Suvadi Works
                <span className="english-subtitle">Simple, Secure, Scalable</span>
              </h2>
              <div className="how-it-works-steps">
                <div className="step-panel">
                  <div className="step-icon">
                    <img src="/icons/ocr-camera.svg" alt="Capture" className="process-icon" />
                  </div>
                  <h3>Capture & Extract</h3>
                  <p>Take a photo of any warranty document. Our AI-powered OCR instantly extracts all relevant details with 99% accuracy.</p>
                </div>
                <div className="step-panel">
                  <div className="step-icon">
                    <img src="/icons/cloud-storage.svg" alt="Store" className="process-icon" />
                  </div>
                  <h3>Store & Organize</h3>
                  <p>Warranties are securely stored in the cloud with smart categorization, advanced search, and automatic backup.</p>
                </div>
                <div className="step-panel">
                  <div className="step-icon">
                    <img src="/icons/smart-analytics.svg" alt="Track" className="process-icon" />
                  </div>
                  <h3>Track & Alert</h3>
                  <p>Get automated reminders before warranties expire and comprehensive analytics for your entire warranty portfolio.</p>
                </div>
              </div>
            </div>
            <div className="how-it-works-visual">
              <div className="product-illustration">
                <div className="demo-screen">
                  <div className="screen-content">
                    <div className="demo-title">Suvadi Dashboard</div>
                    <div className="demo-icon">
                      <img src="/icons/smart-analytics.svg" alt="Dashboard" className="demo-chart" />
                    </div>
                    <div className="demo-stats">
                      <div className="demo-stat">15 Active</div>
                      <div className="demo-stat">3 Expiring</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              What Our Users Say
            </h2>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-rating" aria-label={`${testimonial.rating} out of 5 stars`}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <img key={i} src="/icons/star.svg" alt="" className="star-icon" />
                  ))}
                </div>
                <p className="testimonial-content">"{testimonial.content}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.role}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              Ready to Digitize Your Warranties?
            </h2>
            <p className="cta-description">
              Join thousands of businesses already protecting their warranties with Suvadi's enterprise-grade platform
            </p>
            <div className="cta-actions">
              <button 
                onClick={() => navigate('/register')}
                className="btn-primary cta-button"
                aria-label="Get started with Suvadi for free"
              >
                <span className="btn-text">Get Started Free</span>
                <span className="btn-icon">
                  <img src="/icons/arrow-right.svg" alt="" className="btn-arrow" />
                </span>
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="btn-outline cta-button-secondary"
                aria-label="Sign in to existing account"
              >
                <span className="btn-text">Already have an account?</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="homepage-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/img/icon-f.png" alt="Suvadi" className="footer-logo-img" />
                <span className="brand-text">சுவடி SUVADI</span>
              </div>
              <p className="footer-tagline">
                Professional warranty management for modern businesses
              </p>
            </div>
            
            <div className="footer-links">
              <div className="footer-section">
                <h4>Product</h4>
                <Link to="/login" aria-label="Access your Suvadi dashboard">Dashboard</Link>
                <Link to="/register" aria-label="Start using Suvadi">Get Started</Link>
              </div>
              <div className="footer-section">
                <h4>Support</h4>
                <a href="mailto:support@suvadi.com" aria-label="Contact Suvadi support team">Contact</a>
                <a href="/help" aria-label="Visit Suvadi help center">Help Center</a>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2025 Suvadi. Built with <img src="/icons/heart.svg" alt="love" className="heart-icon" /> for reliable warranty management.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
